import React, { ReactNode, useCallback, useMemo } from "react";
import { Route, Switch } from "react-router";
import { CHANNELS_PARTNER_TYPES } from "../constants/channels";
import { channelsContext } from "../constants/contexts";
import { normalizePath, toChannelsPath } from "../utils/channels";

type ChannelProviderProps = {
    children?: ReactNode;
    allRoutes: string[];
};

export const ChannelProvider: React.FC<ChannelProviderProps> = (
    props: ChannelProviderProps
) => {
    const allRoutesRegex = useMemo(
        () => `(${props.allRoutes.join("|")})`,
        [props.allRoutes]
    );
    return (
        <Switch>
            <Route
                path={allRoutesRegex}
                render={() => <ChannelHandler content={props.children} />}
            />
            <Route
                path={"/empresas/:channel"}
                render={({ match: { params } }) => (
                    <ChannelHandler
                        partnerType={CHANNELS_PARTNER_TYPES.BUSINESS}
                        channel={params.channel}
                        content={props.children}
                    />
                )}
            />
            <Route
                path={"/:channel"}
                render={({ match: { params } }) => (
                    <ChannelHandler
                        partnerType={CHANNELS_PARTNER_TYPES.CUSTOMERS}
                        channel={params.channel}
                        content={props.children}
                    />
                )}
            />
        </Switch>
    );
};

type ChannelHandlerProps = {
    partnerType?: string;
    channel?: string;
    content: ReactNode;
};

const ChannelHandler: React.FC<ChannelHandlerProps> = (
    props: ChannelHandlerProps
) => {
    const _toChannelsPath = useCallback(
        (path: string) =>
            toChannelsPath(path, props.channel, props.partnerType),
        [props.channel, props.partnerType]
    );
    const _normalizePath = useCallback(
        (path: string) => normalizePath(path, props.channel, props.partnerType),
        [props.channel, props.partnerType]
    );
    const contextValue = useMemo(
        () => ({
            channel: props.channel,
            partnerType: props.partnerType,
            toChannelsPath: _toChannelsPath,
            normalizePath: _normalizePath,
        }),
        [props.channel, props.partnerType, _toChannelsPath]
    );
    return (
        <channelsContext.Provider value={contextValue}>
            {props.content}
        </channelsContext.Provider>
    );
};
